import { useEffect, useState } from 'react'
import { apiFetch } from '../lib/api'
import type { ForecastResponse } from '../types'
import { MOCKS, type MockKey } from '../lib/mockData'
import { setApiLang } from '../lib/i18n'

function getMockKey(): MockKey | null {
  if (!import.meta.env.DEV) return null
  const param = new URLSearchParams(window.location.search).get('mock')
  return param && param in MOCKS ? (param as MockKey) : null
}

export function useForecast(mockOverride?: MockKey) {
  const [data, setData] = useState<ForecastResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const mockKey = mockOverride ?? getMockKey()
  const retry = () => { setError(null); setLoading(true); setRetryCount((n) => n + 1) }

  useEffect(() => {
    if (mockKey) {
      setData(MOCKS[mockKey])
      setLoading(false)
      return
    }

    const fetchData = () => {
      apiFetch<ForecastResponse>('/forecast')
        .then((res) => {
          setApiLang(res.lang)
          setData(res)
        })
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false))
    }

    fetchData()

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [mockKey, retryCount])

  return { data, loading, error, retry }
}
